import * as React from "react";
import Component from "src/components/Component";
import withAxios, { IAxiosProps } from "src/components/withAxios";
import resolveUri from "src/resolveUri";
import LevelStep from "./LevelStep";
import EmbedHeader from "./EmbedHeader";
import type { ILevel } from "../levels";

const SPORT_ID = 4;

export interface IEnrollProps extends IAxiosProps {
  schoolSlug?: string;
  schoolLoading: boolean;
  schoolNotFound: boolean;
  schoolId: number | null;
  trainingLevel: number;
  /** Embed URL step (1–4); used after registration to show the correct survey link(s). */
  entryLevel: number | null;
  onAdvanceLevel: () => void;
  onFinishLevels: () => void;
  /** Config for the active training step (levels 1–4); undefined if not on a level screen. */
  levelConfig: ILevel | undefined;
}

interface IEnrollState {
  firstName: string;
  lastName: string;
  coachEmail: string;
  adEmail: string;
  firstNameErr: string;
  lastNameErr: string;
  coachEmailErr: string;
  adEmailErr: string;
  submitting: boolean;
  submitError: string;
  studentUrl: string;
  coachUrl: string;
  coachName: string;
  /** Label of the link row whose Copy button shows “Link copied” feedback. */
  copyFeedbackFor: string | null;
}

class EnrollInner extends Component<IEnrollProps, IEnrollState> {
  public state: IEnrollState = {
    firstName: "",
    lastName: "",
    coachEmail: "",
    adEmail: "",
    firstNameErr: "",
    lastNameErr: "",
    coachEmailErr: "",
    adEmailErr: "",
    submitting: false,
    submitError: "",
    studentUrl: "",
    coachUrl: "",
    coachName: "",
    copyFeedbackFor: null,
  };

  private copyFeedbackResetTimer: number | undefined;

  public componentWillUnmount() {
    if (this.copyFeedbackResetTimer !== undefined) {
      window.clearTimeout(this.copyFeedbackResetTimer);
    }
  }

  public handleSubmit = async () => {
    const { firstName, lastName, coachEmail, adEmail } = this.state;
    const { schoolId, entryLevel } = this.props;
    const embedLevelFields =
      entryLevel != null && entryLevel >= 1 && entryLevel <= 4 ? { entryLevel } : {};

    const firstNameErr = firstName.trim() === "" ? "First name is required" : "";
    const lastNameErr = lastName.trim() === "" ? "Last name is required" : "";
    const coachEmailErr = coachEmail.trim() === "" ? "Email is required" : "";
    const adEmailErr = adEmail.trim() === "" ? "Email is required" : "";

    this.setState({ firstNameErr, lastNameErr, coachEmailErr, adEmailErr, submitError: "" });
    if (firstNameErr || lastNameErr || coachEmailErr || adEmailErr || !schoolId) {
      return;
    }

    const coachNorm = coachEmail.trim().toLowerCase();
    const adNorm = adEmail.trim().toLowerCase();
    const extraEmails: Array<{ email: string }> =
      coachNorm === adNorm ? [{ email: coachNorm }] : [{ email: coachNorm }, { email: adNorm }];

    this.setState({ submitting: true });
    try {
      const coachRes: any = await this.buildPost<any, any>(this, "public/coach")({
        name: `${firstName.trim()} ${lastName.trim()}`,
        extra_emails: extraEmails,
        schoolId,
        active: true,
        invited_role: "coach",
        sports: [SPORT_ID],
        ...embedLevelFields,
      });
      const coachId = coachRes?.id ?? coachRes?.data?.id ?? null;
      const preservedStartDate = coachRes?.preservedStartDate ?? coachRes?.data?.preservedStartDate;

      const surveyRes: any = await this.buildPost<any, any>(this, "survey/Default")({
        schoolId,
        sportId: SPORT_ID,
        coachId,
        // Re-registration keeps the original Champions 101 timing when an old active survey is replaced.
        ...(preservedStartDate ? { preservedStartDate } : {}),
        ...embedLevelFields,
      });

      const survey = surveyRes?.survey ?? surveyRes;
      const base = window.location.origin;
      const studentUrl = resolveUri(`${base}/student/${encodeURIComponent(survey?.studentCode ?? "")}`);
      const coachUrl = resolveUri(`${base}/coach/${encodeURIComponent(survey?.coachCode ?? "")}`);

      this.setState({
        submitting: false,
        studentUrl,
        coachUrl,
        coachName: `${firstName.trim()} ${lastName.trim()}`,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Something went wrong. Please try again.";
      this.setState({ submitError: msg, submitting: false });
    }
  };

  public handleCopy = (url: string, linkLabel: string) => {
    navigator.clipboard.writeText(url).then(() => {
      if (this.copyFeedbackResetTimer !== undefined) {
        window.clearTimeout(this.copyFeedbackResetTimer);
      }
      this.setState({ copyFeedbackFor: linkLabel });
      this.copyFeedbackResetTimer = window.setTimeout(() => {
        this.setState({ copyFeedbackFor: null });
        this.copyFeedbackResetTimer = undefined;
      }, 2500);
    });
  };

  public render() {
    const {
      schoolSlug,
      schoolLoading,
      schoolNotFound,
      trainingLevel,
      entryLevel,
      onAdvanceLevel,
      onFinishLevels,
      levelConfig,
    } = this.props;

    const {
      firstName,
      lastName,
      coachEmail,
      adEmail,
      firstNameErr,
      lastNameErr,
      coachEmailErr,
      adEmailErr,
      submitting,
      submitError,
      studentUrl,
      coachUrl,
      coachName,
      copyFeedbackFor,
    } = this.state;

    const isInvalidLink = !schoolSlug || schoolNotFound;
    const surveyReady = studentUrl !== "";
    const showAcademySteps =
      !schoolLoading && !isInvalidLink && !surveyReady && trainingLevel >= 1 && levelConfig != null;
    const showRegistrationForm = !schoolLoading && !isInvalidLink && !surveyReady && trainingLevel === 0;

    const showCoachSurveyLink =
      entryLevel == null || entryLevel === 1 || entryLevel === 3;
    const showStudentSurveyLink =
      entryLevel == null || entryLevel === 2 || entryLevel === 4;

    const surveyLinkRows: Array<{ label: string; url: string }> = [];
    if (showStudentSurveyLink && studentUrl) {
      surveyLinkRows.push({ label: "Student Survey Link", url: studentUrl });
    }
    if (showCoachSurveyLink && coachUrl) {
      surveyLinkRows.push({ label: "My Self Evaluation Link", url: coachUrl });
    }

    const formHeading = (entryLevel === 1 || entryLevel === 3)? "Time To Take Your Survey" : "Time To Send Your Survey";

    return (
      <>
        {schoolLoading &&
          this.renderBrandedShell(
            <div className="embed-card embed-card--in-shell">
              <div className="embed-loading">
                <div className="embed-spinner" />
                <p>Loading…</p>
              </div>
            </div>
          )}

        {!schoolLoading &&
          isInvalidLink &&
          this.renderBrandedShell(
            <div className="embed-card embed-card--in-shell">
              <div className="embed-alert embed-alert--warning">
                <h2>Invalid Link</h2>
                <p>No school was found for this link. Please contact your administrator for a valid survey link.</p>
              </div>
            </div>
          )}

        {showAcademySteps && levelConfig && (
          <LevelStep
            level={levelConfig}
            onContinue={onAdvanceLevel}
            onFinishToForm={onFinishLevels}
          />
        )}

        {showRegistrationForm &&
          this.renderBrandedShell(
            <div className="embed-card embed-card--in-shell">
              <h2>{formHeading}</h2>
              <p className="embed-subtitle">Please fill in the information below.</p>

              <div className="embed-field">
                <label className="embed-label">First Name</label>
                <input
                  type="text"
                  className="embed-input"
                  placeholder="Enter First Name"
                  name="firstName"
                  value={firstName}
                  onChange={(e) => this.setState({ firstName: e.target.value, firstNameErr: "" })}
                />
                {firstNameErr && <div className="embed-error">{firstNameErr}</div>}
              </div>

              <div className="embed-field">
                <label className="embed-label">Last Name</label>
                <input
                  type="text"
                  className="embed-input"
                  placeholder="Enter Last Name"
                  name="lastName"
                  value={lastName}
                  onChange={(e) => this.setState({ lastName: e.target.value, lastNameErr: "" })}
                />
                {lastNameErr && <div className="embed-error">{lastNameErr}</div>}
              </div>

              <div className="embed-field">
                <label className="embed-label">My Email</label>
                <p className="embed-email-hint">
                  {this.props.entryLevel === 1 || this.props.entryLevel === 3
                    ? "You will receive a copy of the report by email shortly after submission."
                    : "You will receive a copy of the results once it closes in 72 hours."}
                </p>
                <input
                  type="email"
                  className="embed-input"
                  placeholder="Enter Email Address"
                  name="coachEmail"
                  value={coachEmail}
                  onChange={(e) => this.setState({ coachEmail: e.target.value, coachEmailErr: "" })}
                />
                {coachEmailErr && <div className="embed-error">{coachEmailErr}</div>}
              </div>

              <div className="embed-field">
                <label className="embed-label">Athletic Director’s Email</label>
                <p className="embed-email-hint">
                  Add your AD's email to share your coaching development progress.
                </p>
                <input
                  type="email"
                  className="embed-input"
                  placeholder="Enter Email Address"
                  name="adEmail"
                  value={adEmail}
                  onChange={(e) => this.setState({ adEmail: e.target.value, adEmailErr: "" })}
                />
                {adEmailErr && <div className="embed-error">{adEmailErr}</div>}
              </div>

              {submitError && <div className="embed-error embed-submit-error">{submitError}</div>}

              <button className="embed-submit-btn" onClick={this.handleSubmit} disabled={submitting}>
                {submitting ? "Submitting…" : "SUBMIT"}
              </button>
            </div>
          )}

        {!schoolLoading &&
          surveyReady &&
          this.renderBrandedShell(
            <div className="embed-card embed-card--in-shell">
              <div className="embed-alert embed-alert--success">
                <h2>Survey Started!</h2>
                <p>
                  <strong>{coachName}</strong>, you are registered and your survey is ready. Share the links below with the appropriate groups.
                </p>
              </div>

              {surveyLinkRows.map(({ label, url }) => (
                <div key={label} className="embed-link-block">
                  <span className="embed-link-label">{label}</span>
                  <div className="embed-link-row">
                    <input type="text" className="embed-input embed-link-input" readOnly={true} value={url} />
                    <button
                      type="button"
                      className="embed-copy-btn"
                      onClick={() => this.handleCopy(url, label)}
                      aria-label={copyFeedbackFor === label ? "Link copied" : `Copy ${label}`}
                    >
                      {copyFeedbackFor === label ? "Link copied" : "Copy"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </>
    );
  }

  private renderBrandedShell(content: React.ReactNode) {
    return (
      <div className="c101-embed">
        <div className="c101-embed-shell">
          <EmbedHeader />
          <div className="c101-embed-rule" />
          <main className="c101-embed-main embed-branded-main">{content}</main>
        </div>
      </div>
    );
  }
}

const Enroll = withAxios(EnrollInner);

export default Enroll as any;
export { Enroll };



// WEBPACK FOOTER //
// ./src/pages/home/embed/components/Enroll.tsx